import { withErrorBoundary } from 'app/components/error-boundary';
import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Input } from 'app/design/primitives/input';
import { Page } from 'app/design/primitives/page';
import { RemoteError } from 'app/design/primitives/remote-error';
import { Text } from 'app/design/primitives/text';
import { useForm } from 'app/hooks/form';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigationOptions } from 'app/hooks/navigation';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import 'app/lib/form';
import { i18n } from 'app/localization/i18n';
import { useChangePassword } from 'app/network/user';
import type { ChangePasswordDTO } from 'app/types/user';
import type { NavigationScreen } from 'app/types/navigation';
import { usePageTitle } from 'app/utils/web-helpers';
import React, { useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type TextInput } from 'react-native';
import * as yup from 'yup';

type FormSchema = ChangePasswordDTO & { newPasswordConfirmation: string };
const schema: yup.ObjectSchema<FormSchema> = yup.object({
  password: yup.string().password().required(),
  newPassword: yup.string().password().required(),
  newPasswordConfirmation: yup
    .string()
    .oneOf([yup.ref('newPassword'), ''], i18n.t('passwordErrorMatching', { ns: 'common' }))
    .required(),
});

const ChangePasswordScreenComponent: NavigationScreen<'change-password'> = ({ navigation }) => {
  const { t } = useScopedTranslation();
  const { t: tAPI } = useTranslation('api-errors');
  const { control, formState, handleSubmit } = useForm({ schema });
  const [formError, setFormError] = useState<Error>();

  useNavigationOptions(
    navigation,
    {
      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t(`title`)}
        </Text>
      ),
    },
    false,
  );
  const setPageTitle = usePageTitle('change-password');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const refNewPassword1 = useRef<TextInput>(null);
  const refNewPassword2 = useRef<TextInput>(null);

  const changePassword = useChangePassword({
    onSuccess: () => navigation.goBack(),
    onError: (err) => setFormError(err),
  });

  const onSubmit = handleSubmit((form: ChangePasswordDTO) => changePassword.mutate(form));

  return (
    <Page>
      <Column gap="spacing.general.x4">
        <Controller
          name="password"
          {...{ control }}
          render={({ field, fieldState }) => (
            <Input
              autoCapitalize="none"
              autoComplete="password"
              autoCorrect={false}
              error={fieldState.error}
              label={t('passwordFieldLabel')}
              onBlur={field.onBlur}
              onChangeText={field.onChange}
              onSubmitEditing={() => refNewPassword1.current?.focus()}
              placeholder={t('passwordFieldPlaceholder')}
              returnKeyType="next"
              secureTextEntry
              submitBehavior="submit"
              value={field.value}
            />
          )}
        />

        <Controller
          name="newPassword"
          {...{ control }}
          render={({ field, fieldState }) => (
            <Input
              autoCapitalize="none"
              autoComplete="password-new"
              autoCorrect={false}
              error={fieldState.error}
              innerRef={refNewPassword1}
              label={t('newPasswordFieldLabel')}
              onBlur={field.onBlur}
              onChangeText={field.onChange}
              onSubmitEditing={() => refNewPassword2.current?.focus()}
              placeholder={t('newPasswordFieldPlaceholder')}
              returnKeyType="next"
              secureTextEntry
              submitBehavior="submit"
              value={field.value}
            />
          )}
        />

        <Controller
          name="newPasswordConfirmation"
          {...{ control }}
          render={({ field, fieldState }) => (
            <Input
              autoCapitalize="none"
              autoComplete="password-new"
              autoCorrect={false}
              error={fieldState.error}
              innerRef={refNewPassword2}
              label={t('newPasswordConfirmationFieldLabel')}
              onBlur={field.onBlur}
              onChangeText={field.onChange}
              placeholder={t('newPasswordConfirmationFieldPlaceholder')}
              secureTextEntry
              value={field.value}
            />
          )}
        />

        <Button
          disabled={!formState.isDirty || !formState.isValid}
          loading={changePassword.isPending}
          onPress={() => void onSubmit()}
          variant="primary"
        >
          {t('primaryButtonText')}
        </Button>

        {formError ? <RemoteError {...{ error: formError, message: tAPI(formError.message) }} /> : null}
      </Column>
    </Page>
  );
};

export const ChangePasswordScreen: NavigationScreen<'change-password'> = withErrorBoundary(
  withDashboardLayout(ChangePasswordScreenComponent),
);
